import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import type { HomeViewProps } from "./types";
import { PackSpotlight } from "./PackSpotlight";
import { LivePullMarquee } from "./LivePullMarquee";
import { HeroCarousel } from "../HeroCarousel";
import { ProductCard } from "../ProductCard";
import { CategoryCardGrid } from "../storefront/CategoryCardGrid";
import { HorizontalScrollSection, ScrollCard } from "../storefront/HorizontalScrollSection";
import { PackCard } from "../storefront/PackCard";
import { RaffleCard } from "../storefront/RaffleCard";
import { AuctionCard } from "../storefront/AuctionCard";
import { LeaderboardTeaser } from "../storefront/LeaderboardTeaser";
import { SectionHeader } from "../storefront/SectionHeader";
import { Reveal } from "../fx/Reveal";
import { useAuth } from "../../stores/auth";

/**
 * Mobile home — a single thumb-scrollable column. Wide content stays inside
 * horizontal rails so the page itself never scrolls sideways.
 */
export function HomeMobile({ raffles, packs, products, auctions, leaderboard }: HomeViewProps) {
  const { moduleEnabled } = useAuth();
  const openRaffles = raffles.filter((r) => r.status === "OPEN");
  const heroRaffle = openRaffles[0];
  const heroPack = packs[0];
  const liveAuctions = auctions.filter((a) => a.status === "LIVE");

  return (
    <div className="space-y-7 min-w-0 px-4 pt-3">
      <HeroCarousel />

      <CategoryCardGrid />

      <LivePullMarquee />

      {moduleEnabled("packs") && heroPack && (
        <Reveal>
          <PackSpotlight pack={heroPack} size="md" />
        </Reveal>
      )}

      {moduleEnabled("raffles") && heroRaffle && (
        <Reveal as="section" className="min-w-0 space-y-3">
          <SectionHeader title="Live raffles" hint="win the right to buy" to="/raffles" />
          <RaffleCard raffle={heroRaffle} variant="hero" />
          {openRaffles.length > 1 && (
            <div className="scroll-row pb-1">
              {openRaffles.slice(1, 6).map((raffle) => (
                <ScrollCard key={raffle.id} className="w-[78vw] max-w-[320px]">
                  <RaffleCard raffle={raffle} variant="compact" />
                </ScrollCard>
              ))}
            </div>
          )}
        </Reveal>
      )}

      {moduleEnabled("packs") && packs.length > 1 && (
        <Reveal>
          <HorizontalScrollSection title="More mystery boxes" to="/packs">
            {packs.slice(1, 7).map((pack) => (
              <ScrollCard key={pack.id}>
                <PackCard pack={pack} />
              </ScrollCard>
            ))}
          </HorizontalScrollSection>
        </Reveal>
      )}

      {moduleEnabled("ecommerce") && products.length > 0 && (
        <Reveal>
          <HorizontalScrollSection title="Featured products" to="/shop">
            {products.map((product) => (
              <ScrollCard key={product.id}>
                <ProductCard product={product} />
              </ScrollCard>
            ))}
          </HorizontalScrollSection>
        </Reveal>
      )}

      {moduleEnabled("auctions") && liveAuctions.length > 0 && (
        <Reveal as="section" className="space-y-2.5">
          <SectionHeader title="Auctions" hint="ending soon" to="/auctions" />
          {liveAuctions.slice(0, 3).map((auction) => (
            <AuctionCard key={auction.id} auction={auction} />
          ))}
        </Reveal>
      )}

      {leaderboard.length > 0 && (
        <Reveal>
          <LeaderboardTeaser entries={leaderboard} />
        </Reveal>
      )}

      <Reveal as="section" className="glass-panel relative overflow-hidden rounded-2xl p-4">
        <img src="/art/ticket.svg" alt="" className="absolute -right-5 -top-3 w-32 rotate-12 opacity-20" />
        <p className="relative font-display text-xs font-semibold">Track your order</p>
        <p className="relative mt-1 max-w-56 text-[11px] text-white/50">
          Enter your order number anytime — no sign-in needed.
        </p>
        <Link
          to="/track"
          className="relative mt-3 inline-flex items-center gap-1.5 rounded-xl bg-white/10 px-4 py-2 text-xs font-semibold transition-colors hover:bg-white/15"
        >
          Track a parcel <ArrowRight size={13} />
        </Link>
      </Reveal>
    </div>
  );
}
